import { ContextType } from '../../../State/Game/IContext';
import { IGameState } from '../../../State/Game/IGameState';
import { applyDefaultButtonAnimation } from '../UIHelper';
import SectionBaseHandler from './SectionBaseHandler';
import TodoItem from '../../../Prototype/UI/TodoItem';
import Game from '../../../Game';
import ElementComponent from '../../../Component/UI/Element';
import Renderable from '../../../Component/Renderable';
import { PlayerActions } from '../../../State/Player/PlayerActions';
import DateHelper from '../../../Utility/DateHelper';
import { GameActions } from '../../../State/Game/GameActions';
import {
    AdvertisementArea,
    AdvertisementType,
    RewardType,
} from '../../../Utility/SDK/Advertisement';
import GameObject from '../../../GameObject/GameObject';
import { GameObjectEvent } from '../../../GameObject/GameObjectEvent';
import UITextComponent from '../../../Component/UI/Text';
import UIImageComponent from '../../../Component/UI/Image';
import UINineSliceComponent from '../../../Component/UI/NineSlice';
import SideMenuSectionMission from '../../../Prototype/UI/SideMenuSectionMission';
import UIScrollComponent from '../../../Component/UI/Scroll';
import { AUDIO_UI_BUTTON_DEFAULT } from '../../Audio';

interface IMissionItemData {
    title: string;
    progress: number;
    icon: string;
    rewardIcon: string;
    rewardDesc: string;
}

const SECTION = 'main_container/sections';

const MISSION_COOLDOWN = DateHelper.hoursToMilliseconds(8);
const EVENT_COOLDOWN = DateHelper.daysToMilliseconds(7);
const MAX_KEYS = 20;

export default class SectionMissionHandler extends SectionBaseHandler {
    private todoItems: TodoItem[];
    private timerInterval: any;
    private previousKeys: number;

    public handleStateChanged(state: IGameState): void {
        // When complete the weekly event with this menu opened
        // do not update (ToDos and timers) until complete reward keys animation.
        // Wait to fill the key progress bar and hide/close this popup.
        if (this.previousKeys > state.playerState.keys) {
            Game.ticker.clearInterval(this.timerInterval);
            this.timerInterval = null;
        } else {
            this.previousKeys = state.playerState.keys;
            this.updateTimers();
        }
        this.updateToDosList();
    }

    protected onCreate(): void {
        // Instantiate mission section.
        this.section = GameObject.Instantiate(SideMenuSectionMission);
        this.parent.element.find(SECTION).addChild(this.section);

        // Tint key hunt new event nineslice.
        this.section
            .find('key_hunt_new_event')
            .getComponent(UINineSliceComponent).tint = 0xffb16f;

        // Add ToDos items.
        const todoContainer = this.section.find('todos_list');
        this.todoItems = [];
        for (let i = 0; i < 4; i++) {
            const item = GameObject.Instantiate(TodoItem);
            this.todoItems.push(item);
            todoContainer.addChild(item);

            const buttonComplete = item.find('buttonComplete');
            buttonComplete.on(
                GameObjectEvent.SELECTED,
                this.receiveMissionReward.bind(this, i)
            );
            applyDefaultButtonAnimation(
                buttonComplete,
                AUDIO_UI_BUTTON_DEFAULT
            );

            const buttonSkip = item.find('buttonSkip');
            buttonSkip.on(
                GameObjectEvent.SELECTED,
                this.skipMission.bind(this, i)
            );
            applyDefaultButtonAnimation(buttonSkip, AUDIO_UI_BUTTON_DEFAULT);
        }

        // Get current keys.
        this.previousKeys = this.parent.system.state.getState().playerState.keys;

        // Update timers and start interval.
        this.updateKeyHuntHeader();
        this.updateToDosList();
        this.setTimerInterval();
    }

    protected onDestroy(): void {
        Game.ticker.clearInterval(this.timerInterval);
        this.timerInterval = null;

        for (let i = this.todoItems.length - 1; i >= 0; i--) {
            this.todoItems[i]
                .find('buttonComplete')
                .off(GameObjectEvent.SELECTED);
            this.todoItems[i].find('buttonSkip').off(GameObjectEvent.SELECTED);
            this.todoItems[i].destroy();
            this.todoItems[i] = null;
        }
        this.todoItems = null;
    }

    private setTimerInterval(): void {
        this.onTimerInterval();

        if (typeof this.timerInterval === 'function') return;

        this.timerInterval = Game.ticker.setInterval(
            this.onTimerInterval.bind(this),
            1000
        );
    }

    private onTimerInterval(): void {
        const playerState = Game.state.getState().playerState;
        const keyMissionTimerDate = playerState.keyMissionTimer;
        const missionTimerDate = playerState.missionTimer;

        // Update event (key hunt) timer.
        if (keyMissionTimerDate !== null) {
            const eventTime =
                DateHelper.getDateFromValue(keyMissionTimerDate).getTime() -
                DateHelper.now().getTime();

            if (
                eventTime < 0 ||
                (eventTime < EVENT_COOLDOWN &&
                    eventTime + 1000 > EVENT_COOLDOWN)
            ) {
                Game.state.dispatch(PlayerActions.updateTimers());
            }
        }

        // Update missions timer.
        if (
            missionTimerDate !== null &&
            DateHelper.isTimeUp(missionTimerDate, MISSION_COOLDOWN)
        ) {
            Game.state.dispatch(PlayerActions.updateTimers());
        }

        this.updateTimers();
    }

    private updateTimers(): void {
        // Check that element exists - this handles a rare case where
        // updateTimers is called after destroy() despite intervals being "cleared".
        if (!this.section || this.section.destroyed) return;

        const now = DateHelper.now();

        const playerState = Game.state.getState().playerState;
        const keyMissionTimerDate = playerState.keyMissionTimer;
        const missionTimerDate = playerState.missionTimer;

        let missionTimeDiff = 0;
        if (missionTimerDate !== null) {
            const nextMissionTime = DateHelper.getDateFromValue(
                DateHelper.getDateFromValue(missionTimerDate).getTime() +
                    MISSION_COOLDOWN
            );
            missionTimeDiff = nextMissionTime.getTime() - now.getTime();
        }

        let eventTimeDiff = 0;
        if (keyMissionTimerDate !== null) {
            eventTimeDiff =
                DateHelper.getDateFromValue(keyMissionTimerDate).getTime() -
                now.getTime();
        }

        const keyHunt = this.section.find('key_hunt');
        const newEvent = this.section.find('key_hunt_new_event');

        if (eventTimeDiff > EVENT_COOLDOWN) {
            keyHunt.getComponent(Renderable).visible = false;
            newEvent.getComponent(Renderable).visible = true;

            newEvent
                .find('new_event_timer')
                .getComponent(UITextComponent).text = DateHelper.format(
                eventTimeDiff - EVENT_COOLDOWN,
                'D:hh:mm:ss'
            );
        } else {
            keyHunt.getComponent(Renderable).visible = true;
            newEvent.getComponent(Renderable).visible = false;

            keyHunt
                .find('timer/key_hunt_timer')
                .getComponent(UITextComponent).text = DateHelper.format(
                eventTimeDiff,
                'D:hh:mm:ss'
            );
        }

        const missionTime = DateHelper.format(missionTimeDiff, 'hh:mm:ss');

        this.todoItems.forEach((item: GameObject) => {
            const timer = item.find('timer');
            if (timer.getComponent(Renderable).visible) {
                timer.getComponent(UITextComponent).text = missionTime;
            }
        });
    }

    private updateKeyHuntHeader(): void {
        const totalKeys = this.parent.system.state.getState().playerState.keys;
        const keys = Math.min(MAX_KEYS, totalKeys);
        const keyHunt = this.section.find('key_hunt');
        keyHunt
            .find('key_hunt_progress_text')
            .getComponent(UITextComponent).text = `${keys}/${MAX_KEYS}`;
        keyHunt
            .find('key_progress/key_hunt_progress_bar')
            .getComponent(ElementComponent).scale.x = keys / MAX_KEYS;
    }

    private updateToDosList(): void {
        const missions = this.getMissions();

        this.todoItems.forEach((child: GameObject, index: number) => {
            const data = missions[index];
            if (data === null || data === undefined) {
                this.showTimer(child);
            } else {
                this.showProgress(child, data);
            }
        });
    }

    private showTimer(item: GameObject): void {
        item.find('title').getComponent(Renderable).visible = false;
        item.find('icon').getComponent(Renderable).visible = false;
        item.find('rewardIcon').getComponent(Renderable).visible = false;
        item.find('rewardDesc').getComponent(Renderable).visible = false;
        item.find('progress').getComponent(Renderable).visible = false;
        item.find('buttonComplete').getComponent(Renderable).visible = false;
        item.find('buttonSkip').getComponent(Renderable).visible = false;
        item.find('wait').getComponent(Renderable).visible = true;
        item.find('timer').getComponent(Renderable).visible = true;
    }

    private showProgress(item: GameObject, data: IMissionItemData): void {
        item.find('title').getComponent(Renderable).visible = true;
        item.find('icon').getComponent(Renderable).visible = true;
        item.find('rewardIcon').getComponent(Renderable).visible = true;
        item.find('rewardDesc').getComponent(Renderable).visible = true;
        item.find('progress').getComponent(Renderable).visible = true;
        item.find('buttonComplete').getComponent(Renderable).visible = true;
        if (Game.state.getState().platform.adsRewarded) {
            item.find('buttonSkip').getComponent(Renderable).visible = true;
        }
        item.find('wait').getComponent(Renderable).visible = false;
        item.find('timer').getComponent(Renderable).visible = false;

        item.find('title').getComponent(UITextComponent).text = data.title;
        item.find('icon').getComponent(UIImageComponent).texture = data.icon;
        /* prettier-ignore */
        item.find('rewardIcon').getComponent(UIImageComponent).texture = data.rewardIcon;
        /* prettier-ignore */
        item.find('rewardDesc').getComponent(UITextComponent).text = data.rewardDesc;
        /* prettier-ignore */
        item.find('progress/bar').getComponent(ElementComponent).scale.x = data.progress;

        if (data.progress >= 1) {
            item.find('buttonComplete').getComponent(Renderable).visible = true;
            item.find('buttonSkip').getComponent(Renderable).visible = false;
        } else {
            /* prettier-ignore */
            item.find('buttonComplete').getComponent(Renderable).visible = false;
            if (Game.state.getState().platform.adsRewarded) {
                item.find('buttonSkip').getComponent(Renderable).visible = true;
            }
        }
    }

    private receiveMissionReward(index: number): void {
        const missions = this.parent.system.state.getState().playerState
            .missions;
        if (missions[index].prize <= 10) {
            // If prize is "keys", reset the scroll to show key hunt progress bar.
            this.section.getComponent(UIScrollComponent).reset = true;
        }
        this.parent.system.state.dispatch(
            PlayerActions.receiveMissionReward(index)
        );
    }

    private skipMission(index: number): void {
        this.parent.system.state.dispatch(
            GameActions.setContext(ContextType.DISPLAY_ADVERTISEMENT, {
                placement: {
                    id: 0 /*changed from Game.config.adRewardedID*/,
                    type: AdvertisementType.REWARDED,
                },
                payload: {
                    g_gameArea: AdvertisementArea.MISSION_SKIP,
                    g_rewardType: RewardType.MISSION_SKIP,
                    missionIndex: index,
                },
            })
        );
    }

    private getMissions(): IMissionItemData[] {
        const missions = this.parent.system.state.getState().playerState
            .missions;
        const missionList: IMissionItemData[] = [];

        for (const mission of missions) {
            const type = Math.abs(mission.type);

            if (type === 0) {
                missionList.push(null);
                continue;
            }

            let rewardIcon: string;
            let rewardDesc: string;
            if (mission.prize > 10) {
                rewardIcon = 'coins_mission';
                rewardDesc = Game.locale.getText('RewardCoins');
            } else {
                rewardIcon = 'key_mission';
                rewardDesc = Game.locale.getText('RewardKeys');
            }

            missionList.push({
                title: Game.locale
                    .getText(`Mission${type}`)
                    .replace('[x]', `${mission.progress}/${mission.target}`),
                progress: Math.min(mission.progress / mission.target, 1),
                icon: `missions_icon${type}`,
                rewardIcon: rewardIcon,
                rewardDesc: rewardDesc.replace('[x]', `x${mission.prize}`),
            });
        }

        return missionList;
    }
}
